import type { SubscriptionStatus } from '@pomoshnik/shared';

/**
 * Чистые хелперы презентации подписки для Экрана 2.11.1 (карточка подписки).
 * Container/Presenter (ADR-0005): здесь — никакого React, только маппинг
 * `SubscriptionStatus` → тексты/тон бейджа + форматирование даты.
 *
 * **Бейдж «Подписка активна»** — тон `active` рендерится цветом `#0D7A2E` на
 * фоне `#e8f5e9` (PRD §2.11.1 + P-022, контраст ≥4.5:1; НЕ `#16A34A`). Это
 * единственное санкционированное исключение из 5 locked-токенов палитры
 * (P-045) — см. design-checklist §3 хендовера 11.A.
 */

export type SubscriptionTone = 'active' | 'expired';

export interface SubscriptionPresentation {
  /** Текст бейджа состояния. */
  badgeText: string;
  /** `active` (зелёный #0D7A2E) | `expired` (warning-тон). */
  tone: SubscriptionTone;
  /** Подпись с датой («Действует до …» / «Истекла …»). Пустая при невалидной дате. */
  dateLabel: string;
  /** Лейбл кнопки оплаты/продления. */
  actionLabel: string;
}

/** Статусы, при которых подписка считается активной (доступ открыт). */
const ACTIVE_STATUSES: ReadonlySet<SubscriptionStatus> = new Set<SubscriptionStatus>([
  'trial',
  'paid',
]);

const BADGE_TEXT: Record<SubscriptionStatus, string> = {
  trial: 'Пробный период',
  paid: 'Подписка активна',
  grace: 'Подписка истекла',
  expired_readonly: 'Подписка истекла',
  archived: 'Подписка в архиве',
};

/** Форматирует ISO 8601 в `ru-RU` «01 июня 2026». Пустая строка при невалидной дате. */
export function formatExpiresDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function getSubscriptionPresentation(
  status: SubscriptionStatus,
  expiresAt: string,
): SubscriptionPresentation {
  const tone: SubscriptionTone = ACTIVE_STATUSES.has(status) ? 'active' : 'expired';
  const dateText = formatExpiresDate(expiresAt);
  const dateLabel = dateText
    ? tone === 'active'
      ? `Действует до ${dateText}`
      : `Истекла ${dateText}`
    : '';
  return {
    badgeText: BADGE_TEXT[status],
    tone,
    dateLabel,
    actionLabel: tone === 'active' ? 'Продлить подписку' : 'Оплатить подписку',
  };
}

/** Инициалы из ФИО для аватара-плейсхолдера (макс 2 символа, верхний регистр). */
export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
